/**
 * Display multiplier for AI credits.
 * Provider cost is computed in dollars, then converted with this multiplier and
 * stored as credits, so everything persisted below is already in credits
 * (1 credit = $0.01). Limits in `PlanLimits.aiCredits` use the same unit.
 */
const AI_CREDITS_DISPLAY_MULTIPLIER = 100;
/**
 * Convert a raw dollar amount to display credits.
 */
function dollarsToCredits(dollars) {
    return Math.floor(dollars * AI_CREDITS_DISPLAY_MULTIPLIER);
}
/**
 * Convert display credits back to a raw dollar amount.
 */
function creditsToDollars(credits) {
    return credits / AI_CREDITS_DISPLAY_MULTIPLIER;
}
/**
 * Maps a usage metric to the key its limit is stored under in `PlanLimits`.
 *
 * Every metric shares its name with the limit key except `aiTokens`, whose
 * limit lives under `aiCredits`. Declared as a full `Record` so that adding a
 * metric fails to compile until it is mapped, rather than silently resolving
 * to no limit at all.
 */
const LIMIT_KEY_BY_METRIC = {
    domains: 'domains',
    pages: 'pages',
    publishedPages: 'publishedPages',
    quizzes: 'quizzes',
    publishedQuizzes: 'publishedQuizzes',
    members: 'members',
    aiTokens: 'aiCredits',
};
/**
 * Registry of all tracked usage metrics.
 */
const USAGE_METRICS = [
    { type: 'domains', limitKey: 'domains', isConsumable: false },
    { type: 'pages', limitKey: 'pages', isConsumable: false },
    { type: 'publishedPages', limitKey: 'publishedPages', isConsumable: false },
    { type: 'quizzes', limitKey: 'quizzes', isConsumable: false },
    {
        type: 'publishedQuizzes',
        limitKey: 'publishedQuizzes',
        isConsumable: false,
    },
    { type: 'members', limitKey: 'members', isConsumable: false },
    { type: 'aiTokens', limitKey: 'aiCredits', isConsumable: true },
];
/**
 * Limits applied when account has no active subscription and grace period has ended.
 */
const EXPIRED_LIMITS = {
    domains: 0,
    pages: 0,
    publishedPages: 0,
    quizzes: 0,
    publishedQuizzes: 0,
    members: 1, // Owner can still access
    aiCredits: 0,
    sessions: 0,
    buffer: 0,
};
/**
 * Limits applied to accounts that have never had a subscription (free tier).
 */
const FREE_LIMITS = {
    domains: 0,
    pages: 0,
    publishedPages: 0,
    quizzes: 0,
    publishedQuizzes: 0,
    members: 1,
    aiCredits: 10 * AI_CREDITS_DISPLAY_MULTIPLIER,
    sessions: 0,
    buffer: 0,
};

export { AI_CREDITS_DISPLAY_MULTIPLIER, EXPIRED_LIMITS, FREE_LIMITS, LIMIT_KEY_BY_METRIC, USAGE_METRICS, creditsToDollars, dollarsToCredits };
//# sourceMappingURL=usage-limits.esm.js.map
